import React, { useEffect, useState } from 'react';
import { taskConfigurations, useFeathers, isPermitted } from '../../app/util';
import { useSelector } from 'react-redux';

import {
  DataDetailToolbar,
  PaginationControl,
  ScrapedContentUpdaterModal,
  SiteFinderUpdaterModal,
  TaskPreviewTable,
  TypeDropdown,
} from '../../components';
import styled from 'styled-components';
import qs from 'qs';
import { loadFullDisplayDataForTasks } from '../../app/util';

import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';


const TaskList = () => {
  const feathers = useFeathers();
  const user = useSelector(state => state.user);
  const { page, taskType, status } = qs.parse(window.location.search, { ignoreQueryPrefix: true });

  // options for status include complete or not-started

  const [ loading, setLoading ] = useState(true);
  const [ runTaskManagerDialogOpen, setRunTaskManagerDialogOpen ] = useState(false);
  const [ runTaskManagerStep, setRunTaskManagerStep ] = useState('confirm'); // 'confirm' | 'running'
  const [ runQueueCleanupDialogOpen, setRunQueueCleanupDialogOpen ] = useState(false);
  const [ runQueueCleanupStep, setRunQueueCleanupStep ] = useState('confirm'); // 'confirm' | 'running'
  const [ scrapedContentUpdaterModalOpen, setScrapedContentUpdaterModalOpen ] = useState(false);
  const [ siteFinderUpdaterModalOpen, setSiteFinderUpdaterModalOpen ] = useState(false);

  const [ taskCount, setTaskCount ] = useState(null);
  const [ tasksMapped, setTasksMapped ] = useState([]);
  const [ tasks, setTasks ] = useState([]);
  const [ error, setError ] = useState(null);

  const pageSize = 30;
  const defaultTaskType = 'review-candidate';

  const loadTasks = async (page, taskType, status) => {
    setLoading(true)
    setTasks([])

    try {
      const pageAsNumber = (page && !isNaN(parseInt(page))) ? parseInt(page) : 1;
      const $skip = Math.ceil(pageAsNumber - 1, 0) * pageSize;

      const statusToReference = status || 'not-started';
      const statusFilter = statusToReference === 'not-started' ? ['in-progress', 'not-started'] : [statusToReference];

      const tasks = await feathers.getService('research-tasks').find({
        query: {
          status: statusFilter,
          type: taskType || defaultTaskType,
          $includeCheckedOut: true,
          $limit: pageSize,
          $skip,
          $sort: {
            priority: -1,
            createdAt: 1
          }
        }
      });

      setTasks(tasks.data)
      setTaskCount(tasks.total)

      const tasksMapped = await loadFullDisplayDataForTasks(taskType, feathers, tasks.data);
      setTasksMapped(tasksMapped)
    } catch(err) {
      console.error(err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if(feathers) {
      loadTasks(page, taskType, status)
    }
  }, [ feathers ])

  const canRunTaskManager = isPermitted(user, ['admin', 'super-admin', 'editor']);

  const handleRunTaskManagerClick = () => {
    setRunTaskManagerStep('confirm');
    setRunTaskManagerDialogOpen(true);
  };

  const handleRunTaskManagerConfirm = () => {
    setRunTaskManagerStep('running');
    feathers.getService('task-managers').create({}).catch((err) => {
      console.error(err);
      setError(err);
    });
  };

  const handleRunTaskManagerDialogClose = () => {
    setRunTaskManagerDialogOpen(false);
    setRunTaskManagerStep('confirm');
  };

  const handleRunQueueCleanupClick = () => {
    setRunQueueCleanupStep('confirm');
    setRunQueueCleanupDialogOpen(true);
  };

  const handleRunQueueCleanupConfirm = () => {
    setRunQueueCleanupStep('running');
    feathers.getService('queue-cleanup').create({}).catch((err) => {
      console.error(err);
      setError(err);
    });
  };

  const handleRunQueueCleanupDialogClose = () => {
    setRunQueueCleanupDialogOpen(false);
    setRunQueueCleanupStep('confirm');
  };

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: {
					pathname: `/home`,
				}}}
        navTree={[
          {
            text: 'Home',
            to: {
              pathname: `/home`,
            }
          },
          { text: 'Tasks' }
        ]}
        MoreButton={canRunTaskManager ? [
          {
            text: 'Run task manager',
            onClick: handleRunTaskManagerClick,
          },
          {
            text: 'Run queue cleanup',
            onClick: handleRunQueueCleanupClick,
          },
          {
            text: 'Run site-finder update',
            onClick: () => setSiteFinderUpdaterModalOpen(true),
          },
          {
            text: 'Run scraped-content update',
            onClick: () => setScrapedContentUpdaterModalOpen(true),
          }
        ] : undefined}
      />
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', marginTop: '12px', gap: '12px' }}>
        <TypeDropdown
          value={status || 'not-started'}
          options={[
            { type: 'not-started', title: 'In queue' },
            { type: 'complete', title: 'Completed' },
          ]}
          onChange={(newType) => {
            const newQuery = qs.stringify({ ...qs.parse(window.location.search, { ignoreQueryPrefix: true }), status: newType, page: 1 })
            window.history.pushState({}, '', `${window.location.pathname}?${newQuery}`)
            loadTasks(1, taskType, newType)
          }}
        />
        <TypeDropdown
          value={taskType || defaultTaskType}
          options={Object.keys(taskConfigurations).map(key => ({ type: key, title: taskConfigurations[key].title }))}
          onChange={(newType) => {
            const newQuery = qs.stringify({ ...qs.parse(window.location.search, { ignoreQueryPrefix: true }), taskType: newType, page: 1 })
            window.history.pushState({}, '', `${window.location.pathname}?${newQuery}`)
            loadTasks(1, newType, status)
          }}
        />
      </div>
			<TaskPreviewTable 
        tasks={tasksMapped} 
        loading={loading} 
        actions={[
          'back-to-queue'
        ]}
        style={{ marginTop: '12px' }}
        columnConfig={{ 
          name: {
           hideType: true, 
           showDetailName: true, 
          },
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', marginTop: '12px' }}>
        <PaginationControl
          activePage={page ? parseInt(page) : 1}
          items={taskCount}
          itemsPerPage={pageSize}
          onChange={(page) => {
            const newQuery = qs.stringify({ ...qs.parse(window.location.search, { ignoreQueryPrefix: true }), page })
            window.history.pushState({}, '', `${window.location.pathname}?${newQuery}`)
            loadTasks(page, taskType, status)
          }}
        />
      </div>
      <Dialog
        open={runTaskManagerDialogOpen}
        onClose={handleRunTaskManagerDialogClose}
        maxWidth="sm"
        fullWidth
      >
        {runTaskManagerStep === 'confirm' ? (
          <>
            <DialogTitle>
              <Typography variant="h6">Run task manager</Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                This will run the task manager, which will slow down the system temporarily. This should be used sparingly.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleRunTaskManagerDialogClose}>
                Cancel
              </Button>
              <Button
                onClick={handleRunTaskManagerConfirm}
                variant="contained"
                color="primary"
              >
                Run
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={handleRunTaskManagerDialogClose} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent style={{ textAlign: 'center', paddingBottom: '24px' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
              <Typography variant="h6" sx={{ marginTop: 2, fontWeight: 500 }}>
                Running!
              </Typography>
            </DialogContent>
          </>
        )}
      </Dialog>
      <Dialog
        open={runQueueCleanupDialogOpen}
        onClose={handleRunQueueCleanupDialogClose}
        maxWidth="sm"
        fullWidth
      >
        {runQueueCleanupStep === 'confirm' ? (
          <>
            <DialogTitle>
              <Typography variant="h6">Run queue cleanup</Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                This will find candidates stuck in the queue (find-websites and profile) and trigger the appropriate tasks.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleRunQueueCleanupDialogClose}>
                Cancel
              </Button>
              <Button
                onClick={handleRunQueueCleanupConfirm}
                variant="contained"
                color="primary"
              >
                Run
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={handleRunQueueCleanupDialogClose} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent style={{ textAlign: 'center', paddingBottom: '24px' }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
              <Typography variant="h6" sx={{ marginTop: 2, fontWeight: 500 }}>
                Running!
              </Typography>
            </DialogContent>
          </>
        )}
      </Dialog>
      <SiteFinderUpdaterModal
        open={siteFinderUpdaterModalOpen}
        onClose={() => setSiteFinderUpdaterModalOpen(false)}
      />
      <ScrapedContentUpdaterModal
        open={scrapedContentUpdaterModalOpen}
        onClose={() => setScrapedContentUpdaterModalOpen(false)}
      />
      <Snackbar
          open={Boolean(error)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={ 6000 }
          style={{position: 'absolute'}}
          onClose={() => setError(undefined)}
        >
          <Alert
            severity="error"
            onClose={() => setError(undefined)}
            elevation={6}
            variant="filled"
          >
            {error?.message ?? `${error}`}
          </Alert>
        </Snackbar>
    </Wrapper>
  )
}


const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  min-height: calc(100% - 24px * 2);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;


export default TaskList;
